import { useMemo, type ComponentType, type PropsWithChildren } from 'react';
import {
  ChevronsUpDownIcon,
  HomeIcon,
  LogOutIcon,
  MapPinIcon,
  MoonIcon,
  SunIcon,
  TargetIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { authClient } from '@/lib/auth-client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { getDefaultAuthenticatedPath } from '@/lib/navigation';
import { useThemeCustomizer } from '@/lib/theme-customizer';

type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;
type SessionUserWithRole = SessionData['user'] & { role?: string | null };

type AppSidebarProps = {
  onSignOut: () => Promise<void>;
  session: SessionData;
};

type NavItem = {
  href: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  testId: string;
};

const getPrimaryNavigation = (session: SessionData): NavItem[] => {
  const isAdmin = getUserRole(session) === 'admin';

  return [
    {
      href: '/admin/dashboard',
      icon: HomeIcon,
      label: 'Tableau de bord',
      testId: 'nav-dashboard',
    },
    {
      href: getDefaultAuthenticatedPath(session),
      icon: MapPinIcon,
      label: isAdmin ? 'Régions' : 'Salons',
      testId: 'nav-regions',
    },
    {
      href: '/admin/leads',
      icon: TargetIcon,
      label: 'Leads',
      testId: 'nav-leads',
    },
  ];
};

const administratorNavigation: NavItem[] = [
  {
    href: '/admin/users',
    icon: UsersIcon,
    label: 'Utilisateurs',
    testId: 'nav-admin-users',
  },
];

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function getUserRole(session: SessionData) {
  return ((session.user as SessionUserWithRole).role ?? 'user').toLowerCase();
}

function getPageMeta(session: SessionData, pathname: string) {
  const isAdmin = getUserRole(session) === 'admin';

  if (pathname === '/account') {
    return {
      group: 'Menu principal',
      page: 'Mon compte',
    };
  }

  if (pathname === '/admin/regions/new') {
    return {
      group: 'Menu principal',
      parent: {
        href: '/admin/regions',
        label: 'Régions',
      },
      page: 'Ajouter une région',
    };
  }

  if (pathname.includes('/salons') && pathname.includes('/regions/')) {
    return {
      group: 'Menu principal',
      parent: isAdmin ? {
        href: '/admin/regions',
        label: 'Régions',
      } : undefined,
      page: 'Gestion des Salons',
    };
  }

  if (pathname.startsWith('/admin/regions')) {
    return {
      group: 'Menu principal',
      page: isAdmin ? 'Régions' : 'Salons',
    };
  }

  if (pathname === '/admin/users/new') {
    return {
      group: 'Administrateur',
      parent: {
        href: '/admin/users',
        label: 'Utilisateurs',
      },
      page: 'Ajouter un membre',
    };
  }

  if (/^\/admin\/users\/[^/]+$/.test(pathname)) {
    return {
      group: 'Administrateur',
      parent: {
        href: '/admin/users',
        label: 'Utilisateurs',
      },
      page: 'Modifier un membre',
    };
  }

  if (pathname.startsWith('/admin/users')) {
    return {
      group: 'Administrateur',
      page: 'Utilisateurs',
    };
  }

  if (pathname.startsWith('/admin/leads')) {
    return {
      group: 'Menu principal',
      page: 'Leads',
    };
  }

  return {
    group: 'Menu principal',
    page: 'Tableau de bord',
  };
}

function AppBrand() {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton asChild size="lg" className="justify-center hover:bg-transparent">
          <Link to="/">
            <div className="flex h-12 w-32 items-center justify-center">
              <img src="/logo-mutuale.png" alt="Mutuale Logo" className="h-full w-auto object-contain" />
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function SidebarNavSection({
  items,
  title,
}: {
  items: NavItem[];
  title: string;
}) {
  const location = useLocation();

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="px-3 text-xs font-semibold tracking-normal text-sidebar-foreground/70">
        {title}
      </SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => {
          const isActive = item.href === '/'
            ? location.pathname === item.href
            : location.pathname.startsWith(item.href);

          return (
            <SidebarMenuItem key={item.href}>
              <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                <NavLink to={item.href} data-testid={item.testId}>
                  <item.icon />
                  <span>{item.label}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavUser({
  onSignOut,
  session,
}: AppSidebarProps) {
  const { isMobile } = useSidebar();
  const initials = useMemo(() => getInitials(session.user.name), [session.user.name]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="user-menu-trigger" asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-9 ring-1 ring-sidebar-border ring-offset-2 ring-offset-sidebar">
                <AvatarImage src={session.user.image ?? undefined} alt={session.user.name} />
                <AvatarFallback className="bg-sidebar-accent font-semibold text-sidebar-foreground">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{session.user.name}</span>
                <span className="truncate text-xs">{session.user.email}</span>
              </div>
              <ChevronsUpDownIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            data-testid="user-menu-content"
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'top'}
            align="end"
            sideOffset={8}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-3 px-2 py-2 text-left text-sm">
                <Avatar className="size-9 ring-1 ring-border ring-offset-2 ring-offset-popover">
                  <AvatarImage src={session.user.image ?? undefined} alt={session.user.name} />
                  <AvatarFallback className="bg-muted font-semibold text-foreground">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-foreground">{session.user.name}</span>
                  <span className="mt-0.5 truncate text-xs text-muted-foreground">{session.user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/account">
                <UserIcon />
                Mon compte
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-action" onClick={() => void onSignOut()}>
              <LogOutIcon />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebarLayout({
  children,
  onSignOut,
  session,
}: PropsWithChildren<AppSidebarProps>) {
  const location = useLocation();
  const pageMeta = getPageMeta(session, location.pathname);
  const isAdmin = getUserRole(session) === 'admin';
  const { effectiveMode, setMode } = useThemeCustomizer();

  return (
    <SidebarProvider>
      <Sidebar
        collapsible="icon"
        className="[&_[data-sidebar=sidebar]]:bg-white [&_[data-slot=sidebar-container]]:border-0 [&_[data-slot=sidebar-container]]:border-r-0 [&_[data-slot=sidebar-container]]:border-l-0 dark:[&_[data-sidebar=sidebar]]:bg-sidebar"
      >
        <SidebarHeader>
          <AppBrand />
        </SidebarHeader>
        <SidebarContent>
          <SidebarNavSection title="Menu principal" items={getPrimaryNavigation(session)} />
          {isAdmin ? <SidebarNavSection title="Administrateur" items={administratorNavigation} /> : null}
        </SidebarContent>
        <SidebarFooter>
          <NavUser onSignOut={onSignOut} session={session} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/70 bg-card/95 backdrop-blur transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-6 lg:px-8">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4 data-[orientation=vertical]:self-center"
            />
            <Breadcrumb>
              <BreadcrumbList>
                {pageMeta.parent ? (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={pageMeta.parent.href}>{pageMeta.parent.label}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                ) : null}
                <BreadcrumbItem>
                  <BreadcrumbPage>{pageMeta.page}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto hidden items-center gap-2 px-6 lg:px-8 md:flex">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setMode(effectiveMode === 'dark' ? 'light' : 'dark')}
              className="rounded-full border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={effectiveMode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
              title={effectiveMode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {effectiveMode === 'dark' ? <SunIcon className="size-4.5" /> : <MoonIcon className="size-4.5" />}
            </Button>
          </div>
        </header>
        <div className="flex min-h-0 flex-1 overflow-hidden bg-background">
          <div className="app-content-shell flex min-w-0 flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto p-6 lg:p-8">
            {children}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
